import React from 'react';
import {Pressable, ScrollView, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import type {ClubEvent, ClubTournamentHighlight} from '@/Hooks/ClubsApi/types';

type Props = {
  event: ClubEvent | null;
  tournaments: ClubTournamentHighlight[];
  isAdmin: boolean;
  variant: 'mobile' | 'rail';
  onEndEvent: () => void;
  onCreateEvent: () => void;
  onTournamentPress: (tournament: ClubTournamentHighlight) => void;
};

const formatStart = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Upcoming';
  }
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function ClubPulse({
  event,
  tournaments,
  isAdmin,
  variant,
  onEndEvent,
  onCreateEvent,
  onTournamentPress,
}: Props) {
  const mobile = variant === 'mobile';
  const count = Number(!!event) + tournaments.length;
  if (!count && !isAdmin) {
    return null;
  }

  const cardWidth = mobile ? 292 : ('100%' as const);
  const cards = (
    <>
      {event ? (
        <View
          testID="club-pulse-event"
          style={{
            width: cardWidth,
            height: mobile ? 68 : undefined,
            minHeight: mobile ? undefined : 72,
            paddingHorizontal: mobile ? 12 : 11,
            paddingVertical: mobile ? 10 : 11,
            borderRadius: mobile ? 12 : 13,
            borderWidth: 1,
            borderColor: '#F1C66D',
            backgroundColor: '#FFF5E2',
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <CCText
              numberOfLines={1}
              style={{
                flex: 1,
                color: '#865510',
                fontSize: mobile ? 11.5 : 12,
                fontWeight: '700',
              }}>
              ⚡ {event.title}
            </CCText>
            <CCText
              style={{
                color: '#D66E19',
                fontSize: mobile ? 9.5 : 10,
                fontWeight: '800',
                marginLeft: 8,
              }}>
              {Math.min(100, event.pct)}%
            </CCText>
          </View>
          <View
            style={{
              height: mobile ? 4 : 5,
              borderRadius: 999,
              backgroundColor: '#F2E2BE',
              overflow: 'hidden',
              marginTop: mobile ? 5 : 9,
            }}>
            <View
              style={{
                width: `${Math.min(100, event.pct)}%`,
                height: '100%',
                borderRadius: 999,
                backgroundColor: '#E17B21',
              }}
            />
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: mobile ? 4 : 7,
            }}>
            <CCText style={{color: '#865510', fontSize: mobile ? 9 : 10}}>
              {event.progress.toLocaleString('en-IN')} /{' '}
              {event.goal.toLocaleString('en-IN')} puzzles
            </CCText>
            {isAdmin ? (
              <Pressable testID="event-end" onPress={onEndEvent} hitSlop={6}>
                <CCText
                  style={{
                    color: '#B36D1F',
                    fontSize: mobile ? 9 : 10,
                    fontWeight: '700',
                  }}>
                  End
                </CCText>
              </Pressable>
            ) : (
              <CCText
                style={{
                  color: '#237B43',
                  fontSize: mobile ? 9 : 10,
                  fontWeight: '700',
                }}>
                You +{event.my_contribution}
              </CCText>
            )}
          </View>
        </View>
      ) : isAdmin ? (
        <Pressable
          testID="event-create-open"
          onPress={onCreateEvent}
          style={{
            width: cardWidth,
            height: mobile ? 60 : undefined,
            minHeight: mobile ? undefined : 72,
            padding: mobile ? 9 : 11,
            borderRadius: mobile ? 12 : 13,
            borderWidth: 1,
            borderStyle: 'dashed',
            borderColor: '#E6B750',
            backgroundColor: '#FFFAEE',
            justifyContent: 'center',
          }}>
          <CCText
            style={{
              color: '#8A5A12',
              fontSize: mobile ? 11.5 : 12,
              fontWeight: '700',
            }}>
            ⚡ Start Puzzle Storm
          </CCText>
          <CCText
            style={{
              color: '#987D58',
              fontSize: mobile ? 9 : 10,
              marginTop: mobile ? 2 : 4,
            }}>
            Rally the club around a shared goal
          </CCText>
        </Pressable>
      ) : null}

      {tournaments.map(tournament => {
        const arena = tournament.tournament_type === 'Circlechess_Arena';
        return (
          <Pressable
            key={tournament.id}
            testID={`club-tournament-${tournament.id}`}
            onPress={() => onTournamentPress(tournament)}
            style={{
              width: cardWidth,
              height: mobile ? 60 : undefined,
              minHeight: mobile ? undefined : 72,
              padding: mobile ? 9 : 11,
              borderRadius: mobile ? 12 : 13,
              borderWidth: 1,
              borderColor: arena
                ? 'rgba(102,98,180,0.35)'
                : 'rgba(61,171,158,0.38)',
              backgroundColor: arena ? '#F4F2FF' : '#EFFAF7',
            }}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <CCText
                numberOfLines={1}
                style={{
                  flex: 1,
                  color: '#332F2D',
                  fontSize: mobile ? 11.5 : 12,
                  fontWeight: '700',
                }}>
                🏆 {tournament.name}
              </CCText>
              <CCText
                style={{
                  color: arena ? '#6662B4' : '#27877C',
                  fontSize: mobile ? 9.5 : 10,
                  fontWeight: '800',
                  marginLeft: 8,
                }}>
                Join →
              </CCText>
            </View>
            <CCText
              numberOfLines={1}
              style={{
                color: arena ? '#7772B8' : '#39968B',
                fontSize: mobile ? 9 : 10,
                marginTop: mobile ? 4 : 7,
              }}>
              {arena ? 'Arena' : 'Swiss'} · {formatStart(tournament.start_date)}
            </CCText>
          </Pressable>
        );
      })}
    </>
  );

  if (mobile) {
    return (
      <View
        testID="club-pulse-mobile"
        style={{paddingTop: 7, paddingBottom: 7}}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingHorizontal: 14,
            marginBottom: 6,
          }}>
          <CCText
            style={{color: '#4D3F37', fontSize: 10.5, fontWeight: '800'}}>
            Club pulse · {count || 1}
          </CCText>
          {count > 1 ? (
            <CCText
              testID="club-pulse-swipe-hint"
              style={{color: '#958C86', fontSize: 9}}>
              Swipe for more →
            </CCText>
          ) : null}
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 7,
            paddingHorizontal: 14,
            paddingRight: 22,
          }}>
          {cards}
        </ScrollView>
      </View>
    );
  }

  return (
    <View
      testID="club-pulse-rail"
      style={{
        padding: 14,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: 'rgba(38,35,34,0.10)',
        backgroundColor: '#fff',
      }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 10,
        }}>
        <View>
          <CCText style={{color: '#262322', fontSize: 13, fontWeight: '700'}}>
            Club pulse
          </CCText>
          <CCText style={{color: '#888280', fontSize: 10.5, marginTop: 2}}>
            What’s happening now and next
          </CCText>
        </View>
        <View
          style={{
            minWidth: 24,
            height: 24,
            paddingHorizontal: 7,
            borderRadius: 12,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(61,171,158,0.12)',
          }}>
          <CCText style={{color: '#287A70', fontSize: 11, fontWeight: '800'}}>
            {count || 1}
          </CCText>
        </View>
      </View>
      <ScrollView
        nestedScrollEnabled
        style={{maxHeight: 232}}
        showsVerticalScrollIndicator={count > 3}
        contentContainerStyle={{gap: 8}}>
        {cards}
      </ScrollView>
    </View>
  );
}
